import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useActions } from './useActions';
import { useTypedSelector } from './useTypedSelector';
import { useDashboardBlockSettings, BlockEffectiveSettings } from './useDashboardBlockSettings';
import { ReportData } from './useReportLoader';
import { unApi } from '../api/endpoints/reports/unApi';
import { DisplayMode, BlockDisplayOptions } from '../types/dashboard';

/**
 * Контекст использования компонента отчёта.
 * - 'report' — страница отчёта (полный режим)
 * - 'dashboard' — блок дашборда (компактный режим)
 * - 'modal' — модальное окно (превью режим)
 */
export type ReportContext = 'report' | 'dashboard' | 'modal';

/**
 * Конфигурация для хука useReportDataProvider.
 */
export interface ReportDataProviderConfig {
  /** Полная конфигурация отчёта (для custom блоков) */
  reportData?: ReportData;
  /** ID связанного сохранённого отчёта (для linked блоков) */
  linkedReportId?: number;
  /** Hash связанного отчёта */
  linkedReportHash?: string;
}

/**
 * Опции для хука useReportDataProvider.
 */
export interface UseReportDataProviderOptions {
  /** Конфигурация отчёта или ссылка на сохранённый отчёт */
  config: ReportDataProviderConfig;
  /** Контекст использования */
  context: ReportContext;
  /** ID блока дашборда (для dashboard/modal контекста) */
  blockId?: string;
  /** Опции отображения */
  displayOptions?: BlockDisplayOptions;
  /** Тип отчёта для определения URL страницы */
  reportType?: 'universal' | 'registration' | 'ad' | 'order' | 'payment' | 'webinar' | 'visit';
  /** Дата блока дашборда (для определения типа отчёта) */
  date?: string;
}

/**
 * Состояние загрузки связанного отчёта.
 */
export interface LinkedReportState {
  /** Загружается ли конфигурация отчёта */
  loading: boolean;
  /** Ошибка загрузки */
  error: Error | null;
  /** Загруженная конфигурация */
  data: ReportData | null;
}

/**
 * Возвращаемые значения хука useReportDataProvider.
 */
export interface UseReportDataProviderReturn {
  /** Эффективная конфигурация (resolved из linked или custom) */
  resolvedConfig: ReportData | null;
  /** Состояние загрузки связанного отчёта */
  linkedReportState: LinkedReportState;
  /** Настройки блока (атрибуция, фильтры и т.д.) */
  blockSettings: BlockEffectiveSettings;
  /** Режим отображения */
  displayMode: DisplayMode;
  /** Флаг: блок связан с сохранённым отчётом */
  isLinkedReport: boolean;
  /** Открыть полный отчёт в модальном окне */
  openFullReportModal: () => void;
  /** Перейти на страницу отчёта */
  navigateToReportPage: () => void;
  /** Получить URL страницы отчёта */
  getReportPageUrl: () => string;
  /** Перезагрузить данные связанного отчёта */
  refetchLinkedReport: () => Promise<void>;
}

/**
 * Определяет тип отчёта по дате блока дашборда.
 */
const getReportTypeFromDate = (date?: string): string => {
  if (!date) return 'universal';
  
  // Формат даты: "registration_2024-01-01_2024-01-31"
  const parts = date.split('_');
  const reportType = parts[0];
  
  switch (reportType) {
    case 'registration':
    case 'universal':
      return 'universal';
    case 'ad':
      return 'ad';
    case 'order':
      return 'order';
    case 'payment':
      return 'payment';
    case 'webinar':
      return 'webinar';
    case 'visit':
      return 'visit';
    default:
      return 'universal';
  }
};

/**
 * Получает URL страницы отчёта по типу.
 */
const getReportPagePath = (reportType: string): string => {
  switch (reportType) {
    case 'ad':
      return '/ad-reports';
    case 'order':
      return '/order-reports';
    case 'payment':
      return '/payment-reports';
    case 'webinar':
      return '/webinar-reports';
    case 'visit':
      return '/reports/visits';
    case 'registration':
    case 'universal':
    default:
      return '/reports/registrations';
  }
};

/**
 * Хук для унифицированного получения данных отчёта.
 * 
 * Объединяет логику из:
 * - useReportLoader — загрузка конфигурации сохранённого отчёта
 * - useDashboardBlockSettings — настройки блока дашборда
 * - Логику выбора Redux state по контексту (report/dashboard/modal)
 * 
 * @example
 * // Для блока дашборда, связанного с отчётом
 * const { resolvedConfig, displayMode, navigateToReportPage } = useReportDataProvider({
 *   config: { linkedReportId: 123 },
 *   context: 'dashboard',
 *   blockId: 'block-1',
 * });
 * 
 * @example
 * // Для модального окна с кастомной конфигурацией
 * const { resolvedConfig, blockSettings } = useReportDataProvider({
 *   config: { reportData: customConfig },
 *   context: 'modal',
 * });
 */
export const useReportDataProvider = (
  options: UseReportDataProviderOptions
): UseReportDataProviderReturn => {
  const {
    config,
    context,
    blockId,
    displayOptions,
    reportType: propReportType,
    date,
  } = options;

  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Определяем, связан ли блок с сохранённым отчётом
  const isLinkedReport = !!(config.linkedReportId || config.linkedReportHash);

  // Состояние загрузки связанного отчёта
  const [linkedReportState, setLinkedReportState] = useState<LinkedReportState>({
    loading: false,
    error: null,
    data: null,
  });

  // Получаем настройки блока через существующий хук
  const blockSettings = useDashboardBlockSettings(blockId, {
    isModal: context === 'modal',
    skip: context === 'report',
  });

  // Определяем режим отображения
  const displayMode: DisplayMode = useMemo(() => {
    switch (context) {
      case 'report':
        return 'full';
      case 'dashboard':
        return displayOptions?.compact ? 'compact' : 'compact';
      case 'modal':
        return 'preview';
      default:
        return 'full';
    }
  }, [context, displayOptions?.compact]);

  // Определяем тип отчёта
  const reportType = useMemo(() => {
    if (propReportType) return propReportType;
    return getReportTypeFromDate(date);
  }, [propReportType, date]);

  /**
   * Загружает конфигурацию связанного отчёта.
   */
  const fetchLinkedReport = useCallback(async () => {
    if (!isLinkedReport) return;

    // Отменяем предыдущий запрос
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLinkedReportState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      let response;
      
      if (config.linkedReportHash) {
        // Загружаем по hash
        response = await unApi.getReportDataFromHash(
          config.linkedReportHash,
          { signal: abortControllerRef.current.signal }
        );
      } else if (config.linkedReportId) {
        // Загружаем по ID (используем hash из списка отчётов)
        // TODO: Добавить API для загрузки по ID если нужно
        throw new Error('Loading by linkedReportId requires linkedReportHash');
      }

      if (response?.data) {
        setLinkedReportState({
          loading: false,
          error: null,
          data: response.data as ReportData,
        });
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return; // Запрос был отменён
      }
      setLinkedReportState({
        loading: false,
        error: error as Error,
        data: null,
      });
    }
  }, [isLinkedReport, config.linkedReportHash, config.linkedReportId]);

  // Загружаем связанный отчёт при монтировании или изменении конфигурации
  useEffect(() => {
    if (isLinkedReport) {
      fetchLinkedReport();
    }

    return () => {
      // Отменяем запрос при размонтировании
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isLinkedReport, fetchLinkedReport]);

  // Эффективная конфигурация (resolved)
  const resolvedConfig = useMemo((): ReportData | null => {
    if (isLinkedReport) {
      return linkedReportState.data;
    }
    return config.reportData || null;
  }, [isLinkedReport, linkedReportState.data, config.reportData]);

  /**
   * Получает URL страницы отчёта с параметрами конфигурации.
   */
  const getReportPageUrl = useCallback((): string => {
    const basePath = getReportPagePath(reportType);
    
    if (isLinkedReport && config.linkedReportHash) {
      // Для связанного отчёта передаём hash
      return `${basePath}?report_hash=${encodeURIComponent(config.linkedReportHash)}`;
    }
    
    if (resolvedConfig) {
      // Для кастомной конфигурации кодируем в base64
      try {
        const configJson = JSON.stringify(resolvedConfig);
        const configBase64 = btoa(encodeURIComponent(configJson));
        return `${basePath}?config=${configBase64}`;
      } catch {
        return basePath;
      }
    }
    
    return basePath;
  }, [reportType, isLinkedReport, config.linkedReportHash, resolvedConfig]);

  /**
   * Переходит на страницу отчёта.
   */
  const navigateToReportPage = useCallback(() => {
    const url = getReportPageUrl();
    navigate(url);
  }, [navigate, getReportPageUrl]);

  /**
   * Открывает полный отчёт в модальном окне.
   * Эта функция должна быть переопределена в компоненте,
   * который управляет модальным окном.
   */
  const openFullReportModal = useCallback(() => {
    // Placeholder — реализация в компоненте дашборда
    console.warn('openFullReportModal should be implemented in the parent component');
  }, []);

  /**
   * Перезагружает данные связанного отчёта.
   */
  const refetchLinkedReport = useCallback(async () => {
    if (isLinkedReport) {
      await fetchLinkedReport();
    }
  }, [isLinkedReport, fetchLinkedReport]);

  return {
    resolvedConfig,
    linkedReportState,
    blockSettings,
    displayMode,
    isLinkedReport,
    openFullReportModal,
    navigateToReportPage,
    getReportPageUrl,
    refetchLinkedReport,
  };
};

export default useReportDataProvider;
